import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Fieldset, Heading, Link, Paragraph, Skeleton, Switch, ValidationMessage } from '@digdir/designsystemet-react';
import type { NotificationEvent, NotificationRole, NotificationSettings } from '../../../api/safetyRounds';
import { Routes } from '../../../enums/Routes';
import {
  useNotificationSettingsQuery,
  useSaveNotificationSettingsMutation,
} from '../../../hooks/queries/safetyRounds/useNotificationSettingsQueries';
import layout from '../../HRDashboard/hmsPage.module.css';
import classes from './Notifications.module.css';

/** The events in the groups the page shows them in */
const GROUPS: Array<{ key: string; events: NotificationEvent[] }> = [
  { key: 'rounds', events: ['ROUND_APPROACHING', 'ROUND_DAY_BEFORE', 'ROUND_OVERDUE', 'TEMPLATE_CHANGED'] },
  { key: 'protocols', events: ['SIGNATURE_WAITING', 'SIGNATURE_OVERDUE', 'PROTOCOL_SENT_BACK'] },
  {
    key: 'deviations',
    events: [
      'DEVIATION_ASSIGNED',
      'DEVIATION_DUE_SOON',
      'DEVIATION_OVERDUE',
      'DEVIATION_SENT_BACK',
      'DEVIATION_REMINDER',
      'DEVIATION_FIXED',
      'DEVIATION_TO_DECISION',
    ],
  },
];

/** The page in each part of the app: the safety representative's, or the general manager's own */
export type NotificationContext = 'safetyRep' | 'generalManager';

/**
 * The general manager sees the emails for their role and as responsible for deviations. The safety representative's
 * page has the rest, so a person with both roles finds each email in the part of the app it belongs to.
 */
export const shownIn = (context: NotificationContext, roles: NotificationRole[] | undefined) =>
  !roles ||
  (context === 'generalManager'
    ? roles.some((role) => role === 'generalManager' || role === 'responsible')
    : roles.some((role) => role !== 'generalManager'));

/** «Mine varsler»: which emails about safety rounds the user gets. The work always shows in the app. */
export const NotificationSettingsPage = ({ role = 'safetyRep' }: { role?: NotificationContext }): ReactElement => {
  const { t } = useTranslation();
  const K = 'safetyRounds.notifications';
  const { data, isLoading, error } = useNotificationSettingsQuery();
  const manager = role === 'generalManager';

  const content = () => {
    if (isLoading)
      return (
        <div aria-busy="true">
          <span className="ds-sr-only">{t(`${K}.loading`)}</span>
          <Skeleton variant="rectangle" width="100%" height={320} />
        </div>
      );
    if (error || !data)
      return (
        <Alert data-color="danger">
          <Heading level={2} data-size="2xs">
            {t('safetyRounds.errors.loadTitle')}
          </Heading>
          <Paragraph>{t('safetyRounds.errors.loadMessage')}</Paragraph>
        </Alert>
      );
    return <Choices settings={data} context={role} />;
  };

  return (
    <div className={layout.page} data-size="md">
      <section className={layout.hero}>
        <div aria-hidden="true" className={layout.backdrop} />
        <div className={layout.heroContent}>
          {manager && (
            <Link asChild data-size="sm" className={layout.startAligned}>
              <RouterLink to={Routes.GeneralManagerTasks}>{t('safetyRounds.protocol.backToTasks')}</RouterLink>
            </Link>
          )}
          <span className={layout.eyebrow}>{manager ? t('safetyRounds.tasks.generalManager.eyebrow') : t(`${K}.eyebrow`)}</span>
          <Heading level={1} data-size="xl">
            {t(`${K}.title`)}
          </Heading>
          <Paragraph className={layout.intro}>{manager ? t(`${K}.introGeneralManager`) : t(`${K}.intro`)}</Paragraph>
        </div>
      </section>
      <div className={classes.content}>{content()}</div>
    </div>
  );
};

const Choices = ({ settings, context }: { settings: NotificationSettings; context: NotificationContext }): ReactElement => {
  const { t } = useTranslation();
  const K = 'safetyRounds.notifications';
  const save = useSaveNotificationSettingsMutation();
  // The choice shows at once. If it fails, the page shows what is saved.
  const sent = !save.isError ? save.variables : undefined;
  const choices = settings.events.map((choice) => ({
    ...choice,
    email: sent?.find((entry) => entry.event === choice.event)?.email ?? choice.email,
  }));
  // Only the event and the choice are sent; the roles are the server's
  const toggle = (event: NotificationEvent) =>
    save.mutate(choices.map((choice) => ({ event: choice.event, email: choice.event === event ? !choice.email : choice.email })));
  const shown = (events: NotificationEvent[]) =>
    choices.filter((choice) => events.includes(choice.event) && shownIn(context, choice.roles));

  return (
    <>
      <Alert data-color="info" data-size="sm">
        <Paragraph data-size="sm">{t(`${K}.inApp`)}</Paragraph>
      </Alert>
      {GROUPS.map((group) => {
        const events = shown(group.events);
        if (events.length === 0) return null;
        return (
          <section key={group.key} className={layout.card} data-gap="sm">
            <Fieldset data-size="sm">
              <Fieldset.Legend>
                <Heading level={2} data-size="xs">
                  {t(`${K}.groups.${group.key}`)}
                </Heading>
              </Fieldset.Legend>
              <Fieldset.Description>{t(`${K}.byEmail`)}</Fieldset.Description>
              {events.map((choice) => (
                <Switch
                  key={choice.event}
                  label={t(`${K}.events.${choice.event}.label`)}
                  description={t(`${K}.events.${choice.event}.when`)}
                  checked={choice.email}
                  onChange={() => toggle(choice.event)}
                />
              ))}
            </Fieldset>
          </section>
        );
      })}
      <div aria-live="polite">
        {save.isSuccess && (
          <Paragraph data-size="xs" role="status">
            {t(`${K}.saved`)}
          </Paragraph>
        )}
        {save.isError && <ValidationMessage data-size="sm">{t(`${K}.failed`)}</ValidationMessage>}
      </div>
    </>
  );
};
